import { DragIndicator } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { ReactNode } from "react";

import SectionHeader from "src-next/shared/ui/sections/SectionHeader";

import type { CannabinoidSectionId } from "./useCannabinoidSectionsLayout";

interface CannabinoidSortableSectionProps {
  id: CannabinoidSectionId;
  title: string;
  subtitle?: string;
  icon: ReactNode;
  badge?: ReactNode;
  headerActions?: ReactNode;
  isCollapsed: boolean;
  onToggleCollapsed: () => void;
  children: ReactNode;
}

/** Seção arrastável e colapsável — mesmo padrão do FluxZap / PatientDash. */
export function CannabinoidSortableSection({
  id,
  title,
  subtitle,
  icon,
  badge,
  headerActions,
  isCollapsed,
  onToggleCollapsed,
  children,
}: CannabinoidSortableSectionProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const dragHandle = (
    <Tooltip title="Arrastar para reordenar" arrow>
      <IconButton
        size="small"
        aria-label="Arrastar para reordenar a seção"
        {...attributes}
        {...listeners}
        sx={{
          cursor: isDragging ? "grabbing" : "grab",
          color: "text.disabled",
          "&:hover": { color: "text.secondary" },
          touchAction: "none",
        }}
      >
        <DragIndicator fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  return (
    <Box
      ref={setNodeRef}
      id={`cannabinoid-section-${id}`}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.45 : 1,
        zIndex: isDragging ? 999 : "auto",
        minWidth: 0,
        width: "100%",
      }}
    >
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: "1px solid",
          borderColor: isDragging ? "primary800.main" : "divider",
          bgcolor: "neutral0.main",
          overflow: "hidden",
          minWidth: 0,
          transition: "border-color 0.15s ease",
        }}
      >
        <CardContent
          sx={{
            py: { xs: 1.5, md: 2 },
            px: { xs: 1.5, md: 2.5 },
            minWidth: 0,
            "&:last-child": { pb: { xs: 1.5, md: 2 } },
          }}
        >
          <SectionHeader
            icon={icon}
            title={title}
            subtitle={subtitle}
            badge={badge}
            isCollapsed={isCollapsed}
            onCollapsedChange={onToggleCollapsed}
            actions={
              <>
                {/* Desktop: Meus pedidos (e demais ações) ficam no cabeçalho. */}
                {headerActions ? (
                  <Box
                    sx={{
                      display: { xs: "none", sm: "flex" },
                      alignItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    {headerActions}
                  </Box>
                ) : null}
                {dragHandle}
              </>
            }
          >
            <Stack spacing={1.25} sx={{ minWidth: 0, width: "100%" }}>
              {/* Mobile: ações (Meus pedidos) em linha própria, abaixo do título. */}
              {headerActions ? (
                <Box
                  onClick={(event) => event.stopPropagation()}
                  sx={{
                    display: { xs: "block", sm: "none" },
                    width: "100%",
                    "& .MuiButton-root": { width: "100%" },
                  }}
                >
                  {headerActions}
                </Box>
              ) : null}
              {children}
            </Stack>
          </SectionHeader>
        </CardContent>
      </Card>
    </Box>
  );
}

/** Chip de progresso dos documentos. */
export function DocumentsProgressBadge({
  uploadedCount,
  totalCount,
}: {
  uploadedCount: number;
  totalCount: number;
}) {
  const complete = uploadedCount === totalCount && totalCount > 0;
  return (
    <Chip
      label={`${uploadedCount}/${totalCount} enviados`}
      size="small"
      color={complete ? "success" : "default"}
      sx={{
        fontWeight: 700,
        height: 24,
        fontSize: "0.8125rem",
        flexShrink: 0,
        maxWidth: "100%",
      }}
    />
  );
}
